import { useMemo, useState } from 'react'
import {
	addMonths,
	eachDayOfInterval,
	endOfMonth,
	endOfWeek,
	format,
	isSameDay,
	isSameMonth,
	startOfMonth,
	startOfWeek,
	subMonths,
} from 'date-fns'
import type { Locale } from 'date-fns'
import { ru } from 'date-fns/locale/ru'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface CalendarEvent {
	id:        string
	date:      Date
	title:     string
	subtitle?: string
	color?:    string
}

export interface EventCalendarProps {
	events:           CalendarEvent[]
	defaultMonth?:    Date
	month?:           Date           // controlled
	onMonthChange?:   (m: Date) => void
	onEventClick?:    (event: CalendarEvent) => void
	className?:       string
	locale?:          Locale
	todayLabel?:      string         // prefix for today's heading, e.g. "Сегодня"
	emptyLabel?:      string         // shown when the selected day has no events
}

/**
 * Compact month grid (a dot marks a day with events) next to the list of
 * events of the selected day.
 */
export function EventCalendar({
	events,
	defaultMonth = new Date(),
	month: monthProp,
	onMonthChange,
	onEventClick,
	className,
	locale = ru,
	todayLabel = 'Сегодня',
	emptyLabel,
}: EventCalendarProps) {
	const [internalMonth, setInternalMonth] = useState<Date>(defaultMonth)
	const [selected, setSelected] = useState<Date>(new Date())
	const month = monthProp ?? internalMonth
	const setMonth = (next: Date) => {
		if (!monthProp) setInternalMonth(next)
		onMonthChange?.(next)
	}

	const days = useMemo(() => {
		const monthStart = startOfMonth(month)
		const monthEnd = endOfMonth(month)
		const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 as const, locale })
		const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 as const, locale })
		return eachDayOfInterval({ start: gridStart, end: gridEnd })
	}, [month, locale])

	const weekdays = days.slice(0, 7).map((d) => format(d, 'EEEEEE', { locale }))

	const eventsByDay = useMemo(() => {
		const map = new Map<string, CalendarEvent[]>()
		for (const ev of events) {
			const key = format(ev.date, 'yyyy-MM-dd')
			const bucket = map.get(key) ?? []
			bucket.push(ev)
			map.set(key, bucket)
		}
		return map
	}, [events])

	const selectedEvents = eventsByDay.get(format(selected, 'yyyy-MM-dd')) ?? []
	const selectedHeading = isSameDay(selected, new Date())
		? `${todayLabel}, ${format(selected, 'd MMMM', { locale })}`
		: format(selected, 'd MMMM', { locale })

	return (
		<div className={cn('grid gap-6 md:grid-cols-[auto_1fr]', className)}>
			<div className="flex flex-col gap-3">
				<div className="flex items-center justify-between gap-2">
					<button
						type="button"
						onClick={() => setMonth(subMonths(month, 1))}
						className="rounded-(--radius-sm) border border-(--color-border) p-1 text-(--color-muted-foreground) hover:bg-(--color-muted)"
						aria-label="Previous month"
					>
						<ChevronLeftIcon className="size-4" />
					</button>
					<span className="text-sm font-[500] capitalize text-(--color-foreground)">
						{format(month, 'LLLL yyyy', { locale })}
					</span>
					<button
						type="button"
						onClick={() => setMonth(addMonths(month, 1))}
						className="rounded-(--radius-sm) border border-(--color-border) p-1 text-(--color-muted-foreground) hover:bg-(--color-muted)"
						aria-label="Next month"
					>
						<ChevronRightIcon className="size-4" />
					</button>
				</div>

				<div className="grid grid-cols-7 gap-y-1 text-center">
					{weekdays.map((d) => (
						<div key={d} className="px-1 pb-1 text-xs capitalize text-(--color-muted-foreground)">
							{d}
						</div>
					))}

					{days.map((day) => {
						const inMonth = isSameMonth(day, month)
						const isSelected = isSameDay(day, selected)
						const hasEvents = eventsByDay.has(format(day, 'yyyy-MM-dd'))
						return (
							<button
								key={day.toISOString()}
								type="button"
								onClick={() => setSelected(day)}
								className={cn(
									'relative mx-auto flex size-9 items-center justify-center rounded-(--radius-md) text-sm transition-colors hover:bg-(--color-muted)',
									inMonth ? 'text-(--color-foreground)' : 'text-(--color-muted-foreground)/60',
									isSelected && 'bg-(--color-muted) font-[600]',
								)}
							>
								{format(day, 'd')}
								{hasEvents && (
									<span aria-hidden className="absolute top-1 size-1 rounded-full bg-(--color-brand-hover)" />
								)}
							</button>
						)
					})}
				</div>
			</div>

			<div className="flex min-w-0 flex-col gap-3 border-t border-(--color-border) pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0">
				<span className="text-center text-sm font-[500] text-(--color-foreground)">{selectedHeading}</span>
				{selectedEvents.length === 0 ? (
					emptyLabel && <p className="text-center text-sm text-(--color-muted-foreground)">{emptyLabel}</p>
				) : (
					<ul className="flex flex-col gap-4">
						{selectedEvents.map((ev) => (
							<li
								key={ev.id}
								onClick={() => onEventClick?.(ev)}
								className={cn(
									'flex gap-3',
									onEventClick && 'cursor-pointer hover:opacity-80 transition-opacity',
								)}
							>
								<span
									aria-hidden
									className="w-1 shrink-0 rounded-full"
									style={{ backgroundColor: ev.color ?? 'var(--color-brand-hover)' }}
								/>
								<div className="flex min-w-0 flex-col gap-0.5">
									<span className="text-sm font-[500] text-(--color-foreground)">{ev.title}</span>
									{ev.subtitle && <span className="text-sm text-(--color-brand)">{ev.subtitle}</span>}
								</div>
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	)
}
